import React, { useMemo } from 'react';
import { type LoadAdsParams, PartnerAdAudience, PartnerAdPlacement } from '@skyslope/partnerships-sdk';
import { PartnershipsProvider } from '@skyslope/partnerships-sdk/react';
import { useSelector } from 'react-redux';
import { DS3_ENV } from '../common/constants';
import type { IRootState } from '../store';
import type { ContactType } from '../store/types';

export enum AdPlacementIds {
  SIGNING = 'ds-signing',
  SIGNED = 'ds-signed',
}

const audienceTypeMap: Partial<Record<ContactType, PartnerAdAudience>> = {
  Seller: PartnerAdAudience.Seller,
  Buyer: PartnerAdAudience.Buyer,
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const useEnvelopeData = () => {
  return useSelector((state: IRootState) => state.envelope.envelope);
};

export function DigiSignAdsProvider({ children }: { children: React.ReactNode }) {
  const envelope = useEnvelopeData();

  const loadAdParams = useMemo((): LoadAdsParams => {
    const audience = envelope?.signer?.metadata?.audienceContactType;
    const audienceType = audience && audienceTypeMap[audience] ? audienceTypeMap[audience] : PartnerAdAudience.Agent;
    return {
      regions: envelope?.metadata?.ownerRegionCountries?.split(',').map((region) => region?.trim()),
      subscriberId: envelope?.metadata?.ownerSubscriberId,
      audience: audienceType,
    };
  }, [envelope]);

  const environment = DS3_ENV || 'prod';
  const loaded =
    loadAdParams.audience != null &&
    loadAdParams.regions != null &&
    loadAdParams.subscriberId != null &&
    environment != null;

  return (
    <PartnershipsProvider
      environment={environment}
      loadAdsParams={loadAdParams}
      ready={loaded}
      defaultPlacement={PartnerAdPlacement.DS3SignerGeneral}
    >
      {children}
    </PartnershipsProvider>
  );
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default DigiSignAdsProvider;
